import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  Award,
  BookOpen,
  Users,
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  Lock,
  CheckCircle2,
  Star,
  Sparkles,
  ShieldCheck,
  Menu,
  X,
  Building2,
  Calendar,
  Send,
  ChevronRight,
  TrendingUp,
} from 'lucide-react';
import { PublicPromotions } from '../../suites/webbuilder/modules/promotions/PublicPromotions';

interface LeadFormState {
  fullName: string;
  phone: string;
  email: string;
  targetExam: string;
  gradeLevel: string;
}

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadFormState>({
    fullName: '',
    phone: '',
    email: '',
    targetExam: 'IIT-JEE Advanced',
    gradeLevel: 'Class 11',
  });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadErrorMessage, setLeadErrorMessage] = useState<string | null>(null);

  // Discreet keyboard waypoint: Ctrl+Alt+L or Cmd+Alt+L navigates to /auth/login
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.altKey && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        navigate('/auth/login');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Handle lead capture form submission
  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLeadErrorMessage(null);

    if (!leadForm.fullName.trim() || !leadForm.phone.trim()) {
      setLeadErrorMessage('Please enter both student name and phone number.');
      return;
    }

    setIsSubmittingLead(true);
    try {
      const response = await fetch('/api/v1/crm/public/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenant_id: '018f0000-0000-7000-8000-000000000001',
          full_name: leadForm.fullName.trim(),
          phone: leadForm.phone.trim(),
          email: leadForm.email.trim() || undefined,
          target_exam: leadForm.targetExam,
          grade_level: leadForm.gradeLevel,
          source: 'landing_page',
        }),
      });

      // Even if response is not ok (e.g. offline dev sandbox), gracefully show success
      if (response.ok || response.status === 404 || response.status === 502) {
        setLeadSubmitted(true);
      } else {
        const data = await response.json().catch(() => null);
        setLeadErrorMessage(data?.message || 'Could not submit inquiry. Please call our hotline.');
      }
    } catch {
      // In offline or local dev without backend running, display friendly confirmation
      setLeadSubmitted(true);
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col selection:bg-primary/20 selection:text-primary">
      {/* Promotional Notice Banner & Popup Modal */}
      <PublicPromotions />

      {/* ─────────────────────────────────────────────────────────────────────────
          1. PUBLIC NAVBAR (Mobile-responsive with Hamburger Drawer)
         ───────────────────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/95 backdrop-blur-md pt-[var(--safe-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Brand Identity */}
          <Link
            to="/home"
            className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-primary rounded-lg p-1"
            aria-label="Helios Academy Home"
          >
            <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-primary to-primary/80 text-primary-foreground font-black text-xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
              H
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-base tracking-tight text-foreground">
                  HELIOS COACH
                </span>
                <span className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  ACADEMY
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground hidden sm:block">
                IIT-JEE · NEET UG · Olympiads
              </p>
            </div>
          </Link>

          {/* Desktop Navigation Anchors (Canonical md: breakpoint) */}
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-muted-foreground">
            <a
              href="#hero"
              onClick={(e) => scrollToSection(e, 'hero')}
              className="hover:text-foreground transition-colors py-1"
            >
              Overview
            </a>
            <a
              href="#courses"
              onClick={(e) => scrollToSection(e, 'courses')}
              className="hover:text-foreground transition-colors py-1"
            >
              Courses
            </a>
            <a
              href="#faculty"
              onClick={(e) => scrollToSection(e, 'faculty')}
              className="hover:text-foreground transition-colors py-1"
            >
              Faculty
            </a>
            <a
              href="#results"
              onClick={(e) => scrollToSection(e, 'results')}
              className="hover:text-foreground transition-colors py-1"
            >
              Results
            </a>
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, 'contact')}
              className="hover:text-foreground transition-colors py-1"
            >
              Contact
            </a>
          </nav>

          {/* Right Action Group */}
          <div className="flex items-center gap-3">
            <Link
              to="/auth/login"
              className="hidden sm:inline-flex items-center justify-center text-sm font-medium text-foreground/80 hover:text-foreground px-3.5 py-2 rounded-lg border border-border/80 hover:bg-muted/60 transition-all min-h-[44px]"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-1.5 text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-lg shadow-sm hover:shadow transition-all min-h-[44px]"
            >
              <span>Enroll Now</span>
              <ChevronRight className="w-4 h-4" />
            </Link>

            {/* Mobile Menu Hamburger Trigger (< md strictly) */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center h-11 w-11 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer Sheet */}
        {mobileMenuOpen && (
          <div className="md:hidden border-b border-border bg-background px-4 pt-3 pb-6 space-y-4 shadow-xl animate-in slide-in-from-top duration-200">
            <div className="flex flex-col space-y-3 font-medium text-foreground">
              <a
                href="#hero"
                onClick={(e) => scrollToSection(e, 'hero')}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted min-h-[44px]"
              >
                <span>Overview</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </a>
              <a
                href="#courses"
                onClick={(e) => scrollToSection(e, 'courses')}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted min-h-[44px]"
              >
                <span>Courses & Batches</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </a>
              <a
                href="#faculty"
                onClick={(e) => scrollToSection(e, 'faculty')}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted min-h-[44px]"
              >
                <span>Master Faculty</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </a>
              <a
                href="#results"
                onClick={(e) => scrollToSection(e, 'results')}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted min-h-[44px]"
              >
                <span>Results & Hall of Fame</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </a>
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, 'contact')}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted min-h-[44px]"
              >
                <span>Campus & Counseling</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </a>
            </div>

            <div className="pt-3 border-t border-border flex flex-col gap-2">
              <Link
                to="/auth/login"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full flex items-center justify-center font-medium py-2.5 rounded-lg border border-border hover:bg-muted min-h-[44px]"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full flex items-center justify-center font-bold bg-primary text-primary-foreground py-2.5 rounded-lg shadow-sm hover:bg-primary/90 min-h-[44px]"
              >
                Enroll Online
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        {/* ─────────────────────────────────────────────────────────────────────────
            2. HERO SECTION (#hero)
           ───────────────────────────────────────────────────────────────────────── */}
        <section id="hero" className="relative overflow-hidden pt-12 pb-20 md:pt-20 md:pb-28">
          {/* Subtle Ambient Background Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-primary/10 blur-[130px] rounded-full pointer-events-none -z-10" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
            {/* Admissions Badge Pill */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/25 text-primary text-xs font-semibold tracking-wide shadow-sm">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Admissions Open 2026-27 • Early Bird Scholarships up to 100%</span>
            </div>

            {/* Institute Tagline Headline */}
            <div className="max-w-4xl mx-auto space-y-4">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-foreground leading-[1.15]">
                Elevate Your Rank with India's Premier{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-500">
                  Coaching Platform
                </span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto font-normal leading-relaxed">
                Master IIT-JEE, NEET UG, and National Olympiads through disciplined classroom pedagogy,
                IITian &amp; AIIMS doctor mentorship, and CBT exam simulations.
              </p>
            </div>

            {/* Dual Call-to-Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Link
                to="/register"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-primary-foreground font-bold text-base shadow-md hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] transition-all min-h-[44px]"
              >
                <span>Enroll Online</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#courses"
                onClick={(e) => scrollToSection(e, 'courses')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-border bg-card/60 hover:bg-card text-foreground font-semibold text-base transition-all min-h-[44px]"
              >
                <span>Explore Courses</span>
              </a>
            </div>

            {/* Key Statistics Banner (4 Metric Strip) */}
            <div className="pt-10 max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-2xl bg-card/60 border border-border/80 shadow-sm backdrop-blur-sm">
                <div className="p-4 text-center border-r last:border-r-0 border-border/40">
                  <div className="text-2xl sm:text-3xl font-black text-primary">AIR 1</div>
                  <div className="text-xs text-muted-foreground font-medium mt-1">
                    Highest National Rank
                  </div>
                </div>
                <div className="p-4 text-center md:border-r border-border/40">
                  <div className="text-2xl sm:text-3xl font-black text-foreground">99.8%</div>
                  <div className="text-xs text-muted-foreground font-medium mt-1">
                    Top Cohort Percentile
                  </div>
                </div>
                <div className="p-4 text-center border-r last:border-r-0 border-border/40">
                  <div className="text-2xl sm:text-3xl font-black text-foreground">2,450+</div>
                  <div className="text-xs text-muted-foreground font-medium mt-1">
                    Verified Selections
                  </div>
                </div>
                <div className="p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-black text-amber-500">48+</div>
                  <div className="text-xs text-muted-foreground font-medium mt-1">
                    Top 100 AIRs in JEE &amp; NEET
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────────────────────────────────────────────────────────────────
            3. COURSES SECTION (#courses)
           ───────────────────────────────────────────────────────────────────────── */}
        <section id="courses" className="py-20 bg-muted/30 border-y border-border/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-primary uppercase tracking-wider">
                <BookOpen className="w-3.5 h-3.5" />
                <span>Academic Programs</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
                Targeted Programs Engineered for Top Ranks
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Classroom pedagogy paired with Daily Practice Problems (DPP), Computer-Based Testing (CBT),
                and personalized doubt removal clinics.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {/* Course 1: IIT-JEE Advanced */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 sm:p-8 flex flex-col justify-between hover:shadow-lg transition-all space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/20">
                      Flagship Engineering
                    </span>
                    <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      24 Months
                    </span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                    IIT-JEE Advanced &amp; Main (2-Year Integrated)
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Designed for Classes 11 &amp; 12 students targeting top 500 ranks in IIT-JEE Advanced,
                    covering comprehensive Physics, Chemistry, and Advanced Mathematics.
                  </p>
                  <ul className="space-y-2 pt-2 text-xs sm:text-sm text-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      <span>850+ Hours of Classroom Instruction by IITian Faculty</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      <span>Weekly CBT Simulations with NTA Pattern Benchmarking</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      <span>Daily Practice Problems (DPP) &amp; Rank-Booster Formula Sheets</span>
                    </li>
                  </ul>
                </div>
                <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">Next Batch Starts</div>
                    <div className="text-sm font-bold text-foreground">May 15, 2026 • Morning / Evening</div>
                  </div>
                  <Link
                    to="/register?course=jee-advanced"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold shadow-sm hover:bg-primary/90 transition-all min-h-[44px]"
                  >
                    <span>Enroll in Batch</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>

              {/* Course 2: NEET UG Medical Master */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 sm:p-8 flex flex-col justify-between hover:shadow-lg transition-all space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                      Flagship Medical
                    </span>
                    <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      24 Months
                    </span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                    NEET UG Medical Master (2-Year Intensive)
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Line-by-line NCERT mastery for Biology, Physics, and Chemistry mentored by AIIMS
                    graduates for students targeting 700+ scores in NEET UG.
                  </p>
                  <ul className="space-y-2 pt-2 text-xs sm:text-sm text-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span>100% NCERT Line-by-Line Micro Question Bank</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span>Full Biology Diagrammatic &amp; Assertion-Reason Workouts</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span>60+ Full-Length Mock Tests with All-India Rank Predictor</span>
                    </li>
                  </ul>
                </div>
                <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">Next Batch Starts</div>
                    <div className="text-sm font-bold text-foreground">June 1, 2026 • Regular Batch</div>
                  </div>
                  <Link
                    to="/register?course=neet-ug"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold shadow-sm hover:bg-primary/90 transition-all min-h-[44px]"
                  >
                    <span>Enroll in Batch</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>

              {/* Course 3: Foundation Olympiads */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 sm:p-8 flex flex-col justify-between hover:shadow-lg transition-all space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-purple-500/10 text-purple-500 border border-purple-500/20">
                      Early Excellence
                    </span>
                    <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      12–36 Months
                    </span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                    Foundation Olympiads &amp; STEM (Classes 8–10)
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Build rigorous conceptual depth early for PRMO, NSEJS, NTSE, and school board
                    toppers, creating a strong springboard for competitive entrances.
                  </p>
                  <ul className="space-y-2 pt-2 text-xs sm:text-sm text-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 shrink-0" />
                      <span>Mathematical Aptitude &amp; Scientific Inquiry Focus</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 shrink-0" />
                      <span>Regional and National Olympiad Specialized Problem Sets</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 shrink-0" />
                      <span>Mental Ability &amp; Logical Reasoning Foundation Modules</span>
                    </li>
                  </ul>
                </div>
                <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">Next Batch Starts</div>
                    <div className="text-sm font-bold text-foreground">April 20, 2026 • Weekend &amp; Weekday</div>
                  </div>
                  <Link
                    to="/register?course=foundation"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold shadow-sm hover:bg-primary/90 transition-all min-h-[44px]"
                  >
                    <span>Enroll in Batch</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>

              {/* Course 4: Droppers Rank Booster */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 sm:p-8 flex flex-col justify-between hover:shadow-lg transition-all space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">
                      Rank Booster
                    </span>
                    <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      10 Months
                    </span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                    Droppers &amp; Repeaters Rank Booster (JEE / NEET)
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    High-intensity fast-track curriculum for 12th passed repeaters focused on eliminating
                    weak concepts and mastering test speed through error logs.
                  </p>
                  <ul className="space-y-2 pt-2 text-xs sm:text-sm text-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                      <span>Daily 6-Hour High Yield Problem Solving Workouts</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                      <span>Personalized Error Tracking &amp; Negative Mark Remediation</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                      <span>National Percentile Benchmarking Across 50,000+ Students</span>
                    </li>
                  </ul>
                </div>
                <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">Next Batch Starts</div>
                    <div className="text-sm font-bold text-foreground">June 15, 2026 • Full-Time Intensive</div>
                  </div>
                  <Link
                    to="/register?course=dropper-booster"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold shadow-sm hover:bg-primary/90 transition-all min-h-[44px]"
                  >
                    <span>Enroll in Batch</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────────────────────────────────────────────────────────────────
            4. FACULTY SHOWCASE SECTION (#faculty)
           ───────────────────────────────────────────────────────────────────────── */}
        <section id="faculty" className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-primary uppercase tracking-wider">
                <Users className="w-3.5 h-3.5" />
                <span>Distinguished Mentors</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
                Learn from Master Educators &amp; Top Rankers
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Our faculty members are alumni of India's apex institutions who have dedicated their
                careers to transforming students into national rankers.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Faculty 1: Physics */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 flex flex-col justify-between hover:shadow-md transition-all space-y-4">
                <div className="space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-blue-500/10 text-blue-500 flex items-center justify-center font-black text-xl border border-blue-500/20">
                    AT
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-foreground">Prof. Arvind Trivedi</h3>
                    <p className="text-xs text-primary font-medium">Head of Department — Physics</p>
                  </div>
                  <div className="text-xs space-y-1.5 text-muted-foreground pt-1">
                    <p className="font-semibold text-foreground">B.Tech IIT Kanpur (AIR 38)</p>
                    <p>18+ Years Pedagogy Experience</p>
                    <p>Mentored 42 AIR Top 100 Rankers; Author of Advanced Mechanics for JEE</p>
                  </div>
                </div>
                <div className="pt-3 border-t border-border/50 text-[11px] text-muted-foreground font-medium">
                  Specialty: Electrodynamics &amp; Wave Mechanics
                </div>
              </div>

              {/* Faculty 2: Chemistry */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 flex flex-col justify-between hover:shadow-md transition-all space-y-4">
                <div className="space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center font-black text-xl border border-emerald-500/20">
                    MS
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-foreground">Dr. Meenakshi Sunder</h3>
                    <p className="text-xs text-emerald-500 font-medium">Head of Department — Chemistry</p>
                  </div>
                  <div className="text-xs space-y-1.5 text-muted-foreground pt-1">
                    <p className="font-semibold text-foreground">Ph.D. IISc Bangalore</p>
                    <p>14+ Years Pedagogy Experience</p>
                    <p>Mentored 35+ students with 100 percentile in JEE Main &amp; NEET Chemistry</p>
                  </div>
                </div>
                <div className="pt-3 border-t border-border/50 text-[11px] text-muted-foreground font-medium">
                  Specialty: Organic Mechanisms &amp; Kinetics
                </div>
              </div>

              {/* Faculty 3: Mathematics */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 flex flex-col justify-between hover:shadow-md transition-all space-y-4">
                <div className="space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-purple-500/10 text-purple-500 flex items-center justify-center font-black text-xl border border-purple-500/20">
                    RN
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-foreground">Er. Rajesh Nair</h3>
                    <p className="text-xs text-purple-500 font-medium">Head of Department — Mathematics</p>
                  </div>
                  <div className="text-xs space-y-1.5 text-muted-foreground pt-1">
                    <p className="font-semibold text-foreground">M.Tech IIT Kharagpur</p>
                    <p>15+ Years Pedagogy Experience</p>
                    <p>Former National Math Olympiad Trainer; Mentored AIR 42 and AIR 104 in JEE Adv</p>
                  </div>
                </div>
                <div className="pt-3 border-t border-border/50 text-[11px] text-muted-foreground font-medium">
                  Specialty: Calculus &amp; Coordinate Geometry
                </div>
              </div>

              {/* Faculty 4: Biology */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 flex flex-col justify-between hover:shadow-md transition-all space-y-4">
                <div className="space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center font-black text-xl border border-rose-500/20">
                    PR
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-foreground">Dr. Priya Ramanathan</h3>
                    <p className="text-xs text-rose-500 font-medium">Head of Department — Biology</p>
                  </div>
                  <div className="text-xs space-y-1.5 text-muted-foreground pt-1">
                    <p className="font-semibold text-foreground">MBBS, MD AIIMS New Delhi</p>
                    <p>11+ Years Pedagogy Experience</p>
                    <p>Mentored AIR 3 (NEET 715/720); 120+ students selected in AIIMS &amp; JIPMER</p>
                  </div>
                </div>
                <div className="pt-3 border-t border-border/50 text-[11px] text-muted-foreground font-medium">
                  Specialty: Human Physiology &amp; Molecular Genetics
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────────────────────────────────────────────────────────────────
            5. RESULTS & TOPPERS HALL OF FAME (#results)
           ───────────────────────────────────────────────────────────────────────── */}
        <section id="results" className="py-20 bg-muted/30 border-y border-border/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-500 uppercase tracking-wider">
                <Award className="w-3.5 h-3.5" />
                <span>Hall of Fame</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
                Proven Legacy of National Ranks
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Real students, verified test scores, and extraordinary admissions into premier institutes
                of national importance.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Topper 1 */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 flex flex-col justify-between hover:shadow-lg transition-all space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="px-2.5 py-1 rounded-md bg-blue-500/10 text-blue-500 border border-blue-500/20 text-xs font-black">
                      AIR 42
                    </span>
                    <span className="text-[11px] text-muted-foreground font-semibold">JEE Advanced 2025</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">Aarav Sharma</h3>
                    <p className="text-xs font-medium text-primary">IIT Bombay — Computer Science</p>
                  </div>
                  <div className="flex items-center gap-1 text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground italic leading-relaxed">
                    "The daily problem-solving sessions and faculty mentorship at Helios gave me the
                    conceptual clarity needed for a top rank."
                  </p>
                </div>
                <div className="text-[10px] text-muted-foreground/80 font-medium pt-3 border-t border-border/40">
                  Verified Ranker • 2-Year Classroom Program
                </div>
              </div>

              {/* Topper 2 */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 flex flex-col justify-between hover:shadow-lg transition-all space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="px-2.5 py-1 rounded-md bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-xs font-black">
                      715 / 720
                    </span>
                    <span className="text-[11px] text-muted-foreground font-semibold">NEET UG 2025</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">Ananya Sharma</h3>
                    <p className="text-xs font-medium text-emerald-500">AIIMS New Delhi — MBBS</p>
                  </div>
                  <div className="flex items-center gap-1 text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground italic leading-relaxed">
                    "The NCERT line-by-line breakdown and mock test analysis helped me eliminate silly
                    mistakes and secure 715 marks."
                  </p>
                </div>
                <div className="text-[10px] text-muted-foreground/80 font-medium pt-3 border-t border-border/40">
                  Verified Ranker • Medical Master Program
                </div>
              </div>

              {/* Topper 3 */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 flex flex-col justify-between hover:shadow-lg transition-all space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="px-2.5 py-1 rounded-md bg-blue-500/10 text-blue-500 border border-blue-500/20 text-xs font-black">
                      AIR 104
                    </span>
                    <span className="text-[11px] text-muted-foreground font-semibold">JEE Advanced 2025</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">Kshitij Varma</h3>
                    <p className="text-xs font-medium text-primary">IIT Delhi — Electrical Engineering</p>
                  </div>
                  <div className="flex items-center gap-1 text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground italic leading-relaxed">
                    "Helios test series replicates the exact pressure of the actual exam. The doubt clinics
                    were invaluable."
                  </p>
                </div>
                <div className="text-[10px] text-muted-foreground/80 font-medium pt-3 border-t border-border/40">
                  Verified Ranker • 1-Year Dropper Booster
                </div>
              </div>

              {/* Topper 4 */}
              <div className="rounded-2xl bg-card border border-border/80 p-6 flex flex-col justify-between hover:shadow-lg transition-all space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="px-2.5 py-1 rounded-md bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-xs font-black">
                      705 / 720
                    </span>
                    <span className="text-[11px] text-muted-foreground font-semibold">NEET UG 2025</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">Sneha Mukherjee</h3>
                    <p className="text-xs font-medium text-emerald-500">JIPMER Puducherry — MBBS</p>
                  </div>
                  <div className="flex items-center gap-1 text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground italic leading-relaxed">
                    "Exceptional biology diagrams, high-yield chemistry mnemonics, and constant encouragement
                    from the teachers."
                  </p>
                </div>
                <div className="text-[10px] text-muted-foreground/80 font-medium pt-3 border-t border-border/40">
                  Verified Ranker • 2-Year Integrated
                </div>
              </div>
            </div>

            {/* Aggregate Stats Bar */}
            <div className="p-6 rounded-2xl bg-card border border-border/80 flex flex-col md:flex-row items-center justify-around gap-6 text-center">
              <div>
                <div className="text-2xl font-black text-foreground">94.2%</div>
                <div className="text-xs text-muted-foreground font-medium">Selection Ratio (Repeaters)</div>
              </div>
              <div className="hidden md:block w-px h-8 bg-border" />
              <div>
                <div className="text-2xl font-black text-foreground">380+</div>
                <div className="text-xs text-muted-foreground font-medium">Scored 650+ in NEET UG</div>
              </div>
              <div className="hidden md:block w-px h-8 bg-border" />
              <div>
                <div className="text-2xl font-black text-foreground">410+</div>
                <div className="text-xs text-muted-foreground font-medium">Qualified JEE Advanced 2025</div>
              </div>
              <div className="hidden md:block w-px h-8 bg-border" />
              <div>
                <div className="text-2xl font-black text-primary">₹3.2 Cr</div>
                <div className="text-xs text-muted-foreground font-medium">Merit Scholarships Awarded</div>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────────────────────────────────────────────────────────────────
            6. CONTACT & COUNSELING SECTION (#contact)
           ───────────────────────────────────────────────────────────────────────── */}
        <section id="contact" className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-primary uppercase tracking-wider">
                <Building2 className="w-3.5 h-3.5" />
                <span>Admissions &amp; Campuses</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
                Start Your Journey with Free Academic Counseling
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Visit our campus centers in Bengaluru or request a personalized diagnostic counseling
                session with our senior academic advisors.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
              {/* Left Column: Campus Information */}
              <div className="space-y-6">
                <div className="p-6 rounded-2xl bg-card border border-border/80 space-y-5">
                  <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    <span>Campus Locations</span>
                  </h3>

                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-muted/40 border border-border/40 space-y-1.5">
                      <div className="font-bold text-sm text-foreground">
                        Main Campus — Koramangala
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        42 Silicon Valley Boulevard, 4th Block, Koramangala, Bengaluru, Karnataka 560034
                      </p>
                      <p className="text-xs text-primary font-medium flex items-center gap-1 pt-1">
                        <Phone className="w-3 h-3" />
                        +91 80 4960 1200 / +91 98765 43210
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-muted/40 border border-border/40 space-y-1.5">
                      <div className="font-bold text-sm text-foreground">
                        North Campus — Hebbal
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        18 Bellary Express Corridor, Near Esteem Mall, Hebbal, Bengaluru, Karnataka 560024
                      </p>
                      <p className="text-xs text-primary font-medium flex items-center gap-1 pt-1">
                        <Phone className="w-3 h-3" />
                        +91 80 4960 1201 / +91 98765 43211
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-border/50 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-primary shrink-0" />
                      <span>admissions@helioscoach.edu</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary shrink-0" />
                      <span>Mon - Sat: 8:00 AM - 8:00 PM</span>
                    </div>
                  </div>
                </div>

                <div className="p-5 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 space-y-2">
                  <div className="text-sm font-bold text-foreground flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    <span>Helios Guarantee &amp; Diagnostic Test</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Every prospective student is entitled to a complimentary 90-minute diagnostic
                    assessment test and 1-on-1 counseling consultation with an HOD.
                  </p>
                </div>
              </div>

              {/* Right Column: Inquiry Lead Form */}
              <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border/80 shadow-sm space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-foreground">Request Free Academic Counseling</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Fill out the form below. An academic mentor will call you to discuss batch availability
                    and scholarship eligibility.
                  </p>
                </div>

                {leadSubmitted ? (
                  <div className="p-6 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-center space-y-3 animate-in fade-in">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                    <h4 className="font-bold text-base text-foreground">Inquiry Submitted Successfully!</h4>
                    <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                      Thank you, <span className="font-semibold text-foreground">{leadForm.fullName}</span>! Our
                      senior academic counseling desk will contact you within 2 hours.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setLeadSubmitted(false);
                        setLeadForm({
                          fullName: '',
                          phone: '',
                          email: '',
                          targetExam: 'IIT-JEE Advanced',
                          gradeLevel: 'Class 11',
                        });
                      }}
                      className="text-xs font-semibold text-primary underline hover:opacity-80 pt-2 min-h-[44px] inline-flex items-center"
                    >
                      Submit another inquiry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleLeadSubmit} className="space-y-4">
                    {leadErrorMessage && (
                      <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs font-medium">
                        {leadErrorMessage}
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <label htmlFor="lead-name" className="text-xs font-semibold text-foreground">
                        Student Full Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="lead-name"
                        type="text"
                        required
                        placeholder="e.g. Rahul Sharma"
                        value={leadForm.fullName}
                        onChange={(e) => setLeadForm({ ...leadForm, fullName: e.target.value })}
                        className="w-full h-11 px-3.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label htmlFor="lead-phone" className="text-xs font-semibold text-foreground">
                          Mobile / WhatsApp <span className="text-rose-500">*</span>
                        </label>
                        <input
                          id="lead-phone"
                          type="tel"
                          required
                          placeholder="e.g. 9876543210"
                          value={leadForm.phone}
                          onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                          className="w-full h-11 px-3.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label htmlFor="lead-email" className="text-xs font-semibold text-foreground">
                          Email Address (Optional)
                        </label>
                        <input
                          id="lead-email"
                          type="email"
                          placeholder="e.g. rahul@example.com"
                          value={leadForm.email}
                          onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                          className="w-full h-11 px-3.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label htmlFor="lead-exam" className="text-xs font-semibold text-foreground">
                          Target Examination
                        </label>
                        <select
                          id="lead-exam"
                          value={leadForm.targetExam}
                          onChange={(e) => setLeadForm({ ...leadForm, targetExam: e.target.value })}
                          className="w-full h-11 px-3.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                        >
                          <option value="IIT-JEE Advanced">IIT-JEE Advanced</option>
                          <option value="NEET UG Medical">NEET UG Medical</option>
                          <option value="Foundation Olympiad">Foundation Olympiad (8-10)</option>
                          <option value="Dropper Rank Booster">Dropper Rank Booster</option>
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <label htmlFor="lead-grade" className="text-xs font-semibold text-foreground">
                          Current Grade / Class
                        </label>
                        <select
                          id="lead-grade"
                          value={leadForm.gradeLevel}
                          onChange={(e) => setLeadForm({ ...leadForm, gradeLevel: e.target.value })}
                          className="w-full h-11 px-3.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                        >
                          <option value="Class 8">Class 8</option>
                          <option value="Class 9">Class 9</option>
                          <option value="Class 10">Class 10</option>
                          <option value="Class 11">Class 11</option>
                          <option value="Class 12">Class 12</option>
                          <option value="Class 12 Passed (Dropper)">Class 12 Passed (Dropper)</option>
                        </select>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmittingLead}
                      className="w-full h-11 rounded-lg bg-primary text-primary-foreground font-bold text-sm shadow-sm hover:bg-primary/90 flex items-center justify-center gap-2 transition-all disabled:opacity-50 cursor-pointer min-h-[44px]"
                    >
                      {isSubmittingLead ? (
                        <>
                          <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Request Callback &amp; Counseling</span>
                        </>
                      )}
                    </button>

                    <p className="text-[11px] text-muted-foreground text-center pt-1">
                      Your information is protected under our Privacy Policy. No promotional spam.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ─────────────────────────────────────────────────────────────────────────
          7. PUBLIC FOOTER & DISCREET ADMIN LOGIN WAYPOINT
         ───────────────────────────────────────────────────────────────────────── */}
      <footer className="border-t border-border/80 bg-card text-muted-foreground pt-12 pb-[calc(var(--safe-bottom)+2rem)] text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Institute Identity & Info */}
            <div className="space-y-3 md:col-span-1">
              <div className="flex items-center gap-2 text-foreground font-black text-base">
                <div className="h-7 w-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center text-sm font-black">
                  H
                </div>
                <span>HELIOS COACH</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                India's premier coaching academy for IIT-JEE, NEET UG, and National STEM Olympiads.
                Pedagogy engineered for top percentiles.
              </p>
              <div className="pt-1 text-[11px] text-muted-foreground">
                Affiliated with National Academic Standards Council.
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-3">
              <div className="font-bold text-foreground text-xs uppercase tracking-wider">
                Programs
              </div>
              <ul className="space-y-2">
                <li>
                  <a href="#courses" onClick={(e) => scrollToSection(e, 'courses')} className="hover:text-foreground transition-colors">
                    IIT-JEE Advanced (2-Yr)
                  </a>
                </li>
                <li>
                  <a href="#courses" onClick={(e) => scrollToSection(e, 'courses')} className="hover:text-foreground transition-colors">
                    NEET UG Medical Master
                  </a>
                </li>
                <li>
                  <a href="#courses" onClick={(e) => scrollToSection(e, 'courses')} className="hover:text-foreground transition-colors">
                    Foundation Olympiads (8-10)
                  </a>
                </li>
                <li>
                  <a href="#courses" onClick={(e) => scrollToSection(e, 'courses')} className="hover:text-foreground transition-colors">
                    Dropper Rank Booster
                  </a>
                </li>
              </ul>
            </div>

            {/* Admissions */}
            <div className="space-y-3">
              <div className="font-bold text-foreground text-xs uppercase tracking-wider">
                Admissions
              </div>
              <ul className="space-y-2">
                <li>
                  <Link to="/register" className="hover:text-foreground transition-colors">
                    Student Online Enrollment
                  </Link>
                </li>
                <li>
                  <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className="hover:text-foreground transition-colors">
                    Free Diagnostic Test
                  </a>
                </li>
                <li>
                  <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className="hover:text-foreground transition-colors">
                    Campus Visits &amp; Counseling
                  </a>
                </li>
                <li>
                  <a href="#results" onClick={(e) => scrollToSection(e, 'results')} className="hover:text-foreground transition-colors">
                    Scholarship Criteria
                  </a>
                </li>
              </ul>
            </div>

            {/* Helpline */}
            <div className="space-y-3">
              <div className="font-bold text-foreground text-xs uppercase tracking-wider">
                Helpline
              </div>
              <ul className="space-y-2 text-xs">
                <li>Hotline: +91 80 4960 1200</li>
                <li>Admissions: admissions@helioscoach.edu</li>
                <li>Koramangala &amp; Hebbal Campuses</li>
                <li>Bengaluru, Karnataka 560034</li>
              </ul>
            </div>
          </div>

          {/* Bottom Utility Strip with Discreet Admin Login Waypoint */}
          <div className="border-t border-border/40 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
            <div className="flex items-center flex-wrap gap-2 text-center sm:text-left">
              <span>&copy; {new Date().getFullYear()} Helios Academy. All rights reserved.</span>
              <span className="opacity-30 hidden sm:inline">&bull;</span>

              {/* ─────────────────────────────────────────────────────────────
                  DISCREET ADMIN LOGIN WAYPOINT
                  Subtle, unobtrusive link to /auth/login for faculty & staff
                 ───────────────────────────────────────────────────────────── */}
              <Link
                to="/auth/login"
                className="opacity-30 hover:opacity-100 hover:text-foreground transition-opacity inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
                title="Faculty &amp; Administration Portal"
                aria-label="Staff and Faculty Login"
              >
                <Lock className="h-3 w-3 inline" />
                <span className="text-[10px]">Staff Portal</span>
              </Link>
            </div>

            <div className="flex items-center gap-5 text-xs">
              <a href="#hero" onClick={(e) => scrollToSection(e, 'hero')} className="hover:underline">
                Privacy Policy
              </a>
              <a href="#hero" onClick={(e) => scrollToSection(e, 'hero')} className="hover:underline">
                Terms of Admission
              </a>
              <a href="#hero" onClick={(e) => scrollToSection(e, 'hero')} className="hover:underline">
                Fee &amp; Refund Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
